import { Component } from '@verndale/core';
import Swiper, { Navigation, Keyboard } from 'swiper';
import 'swiper/css/bundle';
import { DataLayerObject, ExtendedWindowType } from '../helpers/global-types';

class ContentGrid extends Component {
  private swiper: { destroy?: () => void | undefined };
  private galleryItems: HTMLElement[] = [];

  constructor(el: HTMLElement) {
    super(el);
    this.swiper = {};
  }

  setupDefaults() {
    this.dom = {
      $el: this.el,
      $swiper: this.el.querySelector<HTMLElement>(
        '.content-grid__bottom-container'
      ),
      $slides: this.el.querySelectorAll('.swiper-slide'),
      $slideNext: this.el.querySelector<HTMLElement>('.swiper-button-next'),
      $slidePrev: this.el.querySelector<HTMLElement>('.swiper-button-prev'),
      $linkTexts: this.el.querySelectorAll('.content-grid__link-text')
    };
    this.checkBreakpoint();
    this.setGalleryItems();
  }

  addListeners() {
    window.addEventListener('resize', this.handleResize.bind(this));
    this.galleryItems.forEach(galleryItem => {
      // Get the link text element within the gallery item
      const linkText = galleryItem.querySelector('.content-grid__link-text');
      if (!linkText) return;

      // Add mouseover event listener
      linkText.addEventListener(
        'mouseover',
        this.handleTextMouseOver.bind(this)
      );

      // Add mouseout event listener
      linkText.addEventListener('mouseout', this.handleTextMouseOut.bind(this));
    });
  }

  handleResize() {
    if (this.swiper.destroy) {
      this.swiper.destroy();
    }
    this.checkBreakpoint();
  }

  checkBreakpoint() {
    // Destroy Swiper on breakpoint change
    const breakpoint = window.matchMedia('(min-width: 1024px)');
    const destroySwiperOnBreakpoint = (breakpoint: MediaQueryList) => {
      if (breakpoint.matches) {
        if (this.swiper) {
          if (this.swiper.destroy) {
            this.swiper.destroy();
          }
        }
      } else {
        this.initSwiper();
      }
    };
    destroySwiperOnBreakpoint(breakpoint);
  }

  swiperLayerEvent(swiper: Swiper) {
    const interactLayer = this.el.dataset.interactLayer?.replace(
      '{index}',
      (swiper.activeIndex + 1).toString()
    );
    if (interactLayer) {
      (window as ExtendedWindowType).dataLayer.push(
        JSON.parse(interactLayer) as DataLayerObject
      );
    }
  }

  handleVisibleSlides(swiper: Swiper) {
    // detect if visible slides are fully in view
    const visibleSlides = swiper.slides.filter((slide: HTMLElement) =>
      slide.classList.contains('swiper-slide-visible')
    );

    if (visibleSlides.length > 0) {
      const firstVisibleSlide = visibleSlides[0];
      const lastVisibleSlide = visibleSlides[visibleSlides.length - 1];
      const firstVisibleSlideRect = firstVisibleSlide?.getBoundingClientRect();
      const lastVisibleSlideRect = lastVisibleSlide?.getBoundingClientRect();
      const isFirstSlideFullyInView = firstVisibleSlideRect.left >= 0;
      const isLastSlideFullyInView =
        lastVisibleSlideRect.right <= window.innerWidth;

      swiper.slides.forEach((slide: HTMLElement) => {
        const anchor = slide.querySelector('a');

        anchor?.removeAttribute('tabindex');

        if (slide === firstVisibleSlide && !isFirstSlideFullyInView) {
          anchor?.setAttribute('tabindex', '-1');
        }

        if (slide === lastVisibleSlide && !isLastSlideFullyInView) {
          anchor?.setAttribute('tabindex', '-1');
        }
      });
    }
  }

  initSwiper() {
    Swiper.use([Navigation, Keyboard]);
    this.swiper = new Swiper(this.dom.$swiper as HTMLElement, {
      loop: false,
      spaceBetween: 20,
      slidesPerView: 'auto',
      grabCursor: true,
      watchSlidesProgress: true,
      navigation: {
        prevEl: this.dom.$slidePrev as HTMLElement,
        nextEl: this.dom.$slideNext as HTMLElement
      },
      breakpoints: {
        // when window width is >= 320px
        320: {
          centeredSlides: true,
          slidesPerView: 'auto'
        },
        // when window width is >= 640px
        640: {
          slidesOffsetAfter: 65,
          slidesOffsetBefore: 65,
          slidesPerView: 'auto'
        }
      },
      on: {
        afterInit: swiper => {
          setTimeout(() => {
            this.handleVisibleSlides(swiper);
          }, 1000);
        },
        slideChangeTransitionEnd: swiper => this.handleVisibleSlides(swiper),
        slideChange: swiper => this.swiperLayerEvent(swiper)
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true
      }
    });
  }

  handleTextMouseOver(event: Event) {
    const target = event.target as HTMLElement;
    const galleryItem = target.closest('.content-grid__card') as HTMLElement;
    const imageWrapper = galleryItem.querySelector(
      '.content-grid__image-wrapper'
    );
    const hoverImage = (imageWrapper as HTMLElement).style.getPropertyValue(
      '--hover-image'
    );
    const cleanedHoverImage = hoverImage.replace("url('", '').replace("')", '');
    const image = (imageWrapper as HTMLElement).querySelector('img');
    if (image && cleanedHoverImage !== '') {
      const originalImageSrc = image.src;
      image.src = cleanedHoverImage;
      // Add mouseout event listener
      target.addEventListener('mouseout', () => {
        // Restore the original image source
        image.src = originalImageSrc;
      });
    }
  }

  handleTextMouseOut(event: Event) {
    const target = event.target as HTMLElement;
    const galleryItem = target.closest('.content-grid__card') as HTMLElement;
    const imageWrapper = galleryItem.querySelector(
      '.content-grid__image-wrapper'
    );
    const hoverImage = (imageWrapper as HTMLElement).style.getPropertyValue(
      '--hover-image'
    );
    const cleanedHoverImage = hoverImage.replace("url('", '').replace("')", '');
    const image = (imageWrapper as HTMLElement).querySelector('img');
    if (image && cleanedHoverImage !== '') {
      const originalImageSrc = image.src;
      image.src = originalImageSrc;
    }
  }

  setGalleryItems() {
    this.galleryItems = [];
    const cards = document.querySelectorAll('.content-grid__card');
    cards.forEach(card => {
      this.galleryItems.push(card as HTMLElement);
    });
  }
}

export default ContentGrid;
